import { UseLoading } from "@/common/composition/loading";
import { UseMultiSelectionSimpleItems } from "@/common/composition/multi-selection-simple-items";
import { ICONS } from "@/common/icons.map";
import {
  InformationType,
  InfoTypeHelper,
  POPULATION_TYPE,
} from "@/common/models";
import { SharedState } from "@/common/state";
import { emptyFn, groupBy } from "@/fp";
import { computed, Ref, ref, watch } from "@vue/composition-api";
import { BaseDashboardItem } from "../data/models";
import {
  info_type_per_l1,
  get_personal_info_counts,
  calc_ms_labels_per_info_type,
} from "./api";
import { pii_state } from "./pii-dashboard/state";

const subs = {
  time_frame: emptyFn(),
};

const [
  sensitivities,
  toggle_sensitivity,
  selected_sensitivities,
] = UseMultiSelectionSimpleItems({
  items: [0, 1, 2, 3],
  selected: [0, 1, 2, 3],
  always_keep_one_on: true,
});

const [is_loading, set_loading] = UseLoading(false).as_arr();
const _items: Ref<Record<string, BaseDashboardItem>> = ref({});
const other_count = ref(0);

const _regulated_items: Ref<any[]> = ref([]);

const regulated_items = computed(() =>
  _regulated_items.value
    .map(([code, items]) => [
      code,
      items.filter((f: any) =>
        selected_sensitivities.value.includes(f.sensitivity)
      ),
      code == 7 ? 100 : 1,
    ])
    .sort((a, b) => b[2] - a[2])
);

const get_regulated_ids = () => {
  const ids = Object.values(
    SharedState.regulated_info_types_by_categories.value
  )
    .flat()
    .map((d) => {
      // @ts-ignore
      if (d.nested && d.nested.length > 0) {
        // @ts-ignore
        return d.nested.map((sub) => sub.id).concat(d.id);
      } else {
        return [d.id];
      }
    })
    .flat();
  return ids;
};

const load_regulated_data = async () => {
  const res = await get_personal_info_counts(get_regulated_ids());
  if (res.success) {
    const indexed_value = res.value.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
    const all_items = SharedState.regulated_info_types_by_categories.value;
    const mapped_all_items = Object.values(all_items)
      .flat()
      .filter((c) => c.content_code != 8) // 8 is Microsoft Labels
      .map((d) => {
        // @ts-ignore
        let nested = [];
        // @ts-ignore
        if (d.nested) {
          // @ts-ignore
          nested = d.nested.map((n) => {
            return {
              id: n.id,
              name: n.labelName,
              parent_code: n.content_code || 7,
              sensitivity: n.sensitivityLevel,
              count: indexed_value[n.id]?.count || 0,
            };
          });
        }
        return {
          id: d.id,
          name: d.labelName,
          // @ts-ignore
          nested: nested,
          parent_code: d.content_code,
          sensitivity: d.sensitivityLevel,
          count: indexed_value[d.id]?.count || 0,
        };
      })
      .sort((a, b) => b.count - a.count);

    _regulated_items.value = Array.from(
      groupBy("parent_code", mapped_all_items).entries()
    ).sort((a, b) => a[0] - b[0]);
  }
};

const get_other_label = (count: number) => {
  const other_id = SharedState.default_label.value?.id;
  return {
    count,
    id: other_id,
    name: "Other",
    parent_id: other_id,
    type: "LABEL",
  };
};

// TODO: need to account for the change in the sensitivities
// and removed sensitibities param in the call to get all of the items.
const ms_coverage_count: Ref<{
  ms_labels: Record<string, [number, number, number, number]>;
  v_labels: Record<string, [number, number, number, number]>;
}> = ref({ ms_labels: {}, v_labels: {} });

const displayed_ms_count = computed(() => {
  const { ms_labels, v_labels } = ms_coverage_count.value;
  const keep_sensitivities = (_: any, index: number) =>
    selected_sensitivities.value.includes(index);
  const sum_reducer = (acc: number, num: number) => {
    acc += num;
    return acc;
  };
  return SharedState.l0InfoTypes.value.reduce((acc, item) => {
    if (!v_labels[item.id] || !ms_labels[item.id]) {
      acc[item.id] = 0;
    } else {
      const v_labels_sum = v_labels[item.id]
        .filter(keep_sensitivities)
        .reduce(sum_reducer, 0);
      const ms_labels_sum = ms_labels[item.id]
        .filter(keep_sensitivities)
        .reduce(sum_reducer, 0);
      acc[item.id] = (100 * ms_labels_sum) / v_labels_sum;
    }
    return acc;
  }, {} as Record<string, number>);
});

const display_items = computed(() => {
  const result = SharedState.l0InfoTypes.value
    .filter((f) => f.id !== InfoTypeHelper.REGULATED_ID())
    .map((l0) => {
      return {
        id: l0.id,
        order: l0.l0Order,
        name: l0.labelName,
        available: true,
        icon: ICONS.L0_ICONS.get(l0.code),
        items: SharedState.typesByCategories.value
          .get(l0.id)
          .filter(
            (l: any) =>
              l.generation === 1 &&
              selected_sensitivities.value.includes(l.sensitivityLevel)
          )
          .map((l1: InformationType) => {
            return {
              id: l1.id,
              name: l1.labelName,
              count: _items.value[l1.id]?.count || 0,
              parent_id: l1.parentId,
              type: POPULATION_TYPE.LABEL,
            };
          })
          .sort((a: any, b: any) => b.count - a.count),
      };
    })
    .sort((a, b) => (a.order || -1) - (b.order || -1));
  const other_id = SharedState.default_label.value?.id;
  return result.map((d) => {
    if (d.id === other_id) {
      const should_add_other_to_arr = selected_sensitivities.value.includes(
        SharedState.default_label.value?.sensitivityLevel as number
      );
      if (should_add_other_to_arr) {
        return {
          ...d,
          items: [get_other_label(other_count.value), ...d.items],
        };
      } else {
        return {
          ...d,
          items: d.items,
        };
      }
    }
    return d;
  });
});

const load_data = async () => {
  set_loading(true);
  await load_regulated_data();
  const res = await info_type_per_l1();
  pii_state.load_data();
  if (res.success) {
    const { other, l1s } = res.value;
    other_count.value = other;
    _items.value = l1s
      .filter((d) => d.parent_id !== "L0")
      .reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {} as Record<string, BaseDashboardItem>);
  }
  const ms_count_res = await calc_ms_labels_per_info_type();
  if (ms_count_res.success) {
    ms_coverage_count.value = ms_count_res.value;
  }
  set_loading(false);
};

const init = async () => {
  await load_data();
  subs.time_frame = watch(
    SharedState.selectedTimeFrameDisplayName,
    async () => {
      await load_data();
    },
    { lazy: true }
  );
};

const destroy = () => {
  _items.value = {};
  Object.values(subs).forEach((unsub) => unsub());
};

export const state = {
  init,
  selected_sensitivities,
  sensitivities,
  toggle_sensitivity,
  display_items,
  regulated_items,
  ms_coverage_count: displayed_ms_count,
  is_loading,
  destroy,
};
